import { useState, useEffect, useMemo } from 'react';
import { StageInfo, StageGroup } from '../types/stageTypes';
import { groupStagesByType, filterStageGroups } from '../utils/stageGrouping';

/**
 * Custom hook for managing stage groups
 * @param originalStages The original stage data from the API
 * @returns State and functions related to stage grouping
 */
export const useStageGroups = (originalStages: StageInfo[]) => {
    // Stage groups state
    const [stageGroups, setStageGroups] = useState<StageGroup[]>([]);

    // Search query
    const [searchQuery, setSearchQuery] = useState('');

    // Update groups when original stages change
    useEffect(() => {
        if (originalStages.length > 0) {
            setStageGroups(groupStagesByType(originalStages));
        } else {
            setStageGroups([]);
        }
    }, [originalStages]);

    // Filter groups based on search query
    const filteredGroups = useMemo(() => {
        return filterStageGroups(stageGroups, searchQuery);
    }, [stageGroups, searchQuery]);

    // Toggle group expansion
    const toggleGroupExpansion = (groupIndex: number) => {
        setStageGroups(prevGroups => {
            const newGroups = [...prevGroups];
            newGroups[groupIndex] = {
                ...newGroups[groupIndex],
                isExpanded: !newGroups[groupIndex].isExpanded
            };
            return newGroups;
        });
    };

    // Set stage groups directly
    const setGroups = (groups: StageGroup[]) => {
        setStageGroups(groups);
    };

    return {
        stageGroups,
        filteredGroups,
        searchQuery,
        setSearchQuery,
        toggleGroupExpansion,
        setGroups
    };
}; 